import { useEffect, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { env } from '@/utils/env'

const ENCRYPTION_SECRET = env('NEXT_PUBLIC_ENCRYPTION_SECRET') || ''

function hexToBytes(hex: string): Uint8Array {
  if (!hex) return new Uint8Array(32)
  const bytes = new Uint8Array(hex.length / 2)
  for (let i = 0; i < bytes.length; i++) {
    bytes[i] = parseInt(hex.substring(i * 2, i * 2 + 2), 16)
  }
  return bytes
}

function base64UrlToBytes(str: string): Uint8Array {
  str = str.replace(/-/g, '+').replace(/_/g, '/')
  while (str.length % 4 !== 0) {
    str += '='
  }
  const binary = atob(str)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

export async function decryptAsync<T = unknown>(encryptedData: string | [string, string, string]): Promise<T> {
  let ivB64: string
  let tagB64: string
  let ciphB64: string

  if (Array.isArray(encryptedData)) {
    if (encryptedData.length !== 3) {
      throw new Error('Invalid encrypted data array format')
    }
    ;[ivB64, tagB64, ciphB64] = encryptedData
  } else {
    const parts = encryptedData.split('.')
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted data string format. Expected iv.tag.cipher')
    }
    ;[ivB64, tagB64, ciphB64] = parts
  }

  const keyBytes = hexToBytes(ENCRYPTION_SECRET)
  const ivBytes = base64UrlToBytes(ivB64)
  const tagBytes = base64UrlToBytes(tagB64)
  const cipherBytes = base64UrlToBytes(ciphB64)

  const combinedCipher = new Uint8Array(cipherBytes.length + tagBytes.length)
  combinedCipher.set(cipherBytes)
  combinedCipher.set(tagBytes, cipherBytes.length)

  const cryptoKey = await window.crypto.subtle.importKey('raw', keyBytes, { name: 'AES-GCM' }, false, ['decrypt'])

  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: ivBytes,
      tagLength: 128
    },
    cryptoKey,
    combinedCipher
  )

  const decoded = new TextDecoder().decode(decrypted)
  return JSON.parse(decoded) as T
}

export function decrypt<T = unknown>(encryptedData: string | [string, string, string]): T {
  // Sync fallback helper
  try {
    const parts = Array.isArray(encryptedData) ? encryptedData : encryptedData.split('.')
    const ciph = atob(parts[2] || '')
    return JSON.parse(ciph) as T
  } catch {
    return {} as T
  }
}

type UseDecryptedFromPathReturn<T> = {
  data?: T
  error?: string
  isLoading: boolean
}

export function useDecryptedFromPath<T = unknown>(): UseDecryptedFromPathReturn<T> {
  const location = useLocation()
  const params = useParams()

  const [data, setData] = useState<T | undefined>(undefined)
  const [error, setError] = useState<string | undefined>(undefined)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const token = (params.token as string) || searchParams.get('token')

    if (!token || typeof token !== 'string') {
      setError('Token not found in path')
      setIsLoading(false)
      return
    }

    decryptAsync<T>(token)
      .then(decrypted => {
        setData({ ...decrypted, originalToken: token })
      })
      .catch(err => {
        setError(err?.message ?? 'Failed to decrypt token')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [location.search, params])

  return {
    data,
    error,
    isLoading
  }
}
